/**
 * Geräte (P6 Block D) — die Ablösung der alten Ingest-Verwaltung.
 *
 * Die Altseite war ein 3400-Zeilen-Editor mit aufklappbaren Blöcken, in dem
 * Zugangsdaten der Anlagen-Freigaben mitten im Formular standen. Hier gilt
 * dasselbe Muster wie bei den übrigen Arten: eine Liste, die den Zustand auf
 * einen Blick zeigt, und ein Objektblatt je Gerät.
 *
 * Was ein Gerät ist, steht in docs/REVIEW-INGEST-AUSROLLEN-ANBINDUNG.md: ein
 * kleiner Rechner in der Wäscherei, der die Dateien der Taktwaschanlagen von
 * deren Freigabe liest und hierher schickt.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import {
  Karte,
  Kennung,
  Kopfzelle,
  Notiz,
  Tabelle,
  Zelle,
  Zustand,
} from '../../components/leitstand';

interface Geraet {
  id: string;
  deviceId: string;
  hostname?: string | null;
  status: string;
  role?: string | null;
  lastSeenAt?: string | null;
  anlagen?: Array<{ maschine: string; bezeichnung: string; feeds: number }>;
  quelle?: string | null;
}

/** Wie lange darf ein Gerät stumm sein, bevor es auffällt? */
const STUMM_MINUTEN = 30;

const STATUS_TEXT: Record<string, string> = {
  APPROVED: 'freigegeben',
  PENDING: 'wartet auf Freigabe',
  REJECTED: 'abgelehnt',
  BANNED: 'gesperrt',
  DECOMMISSIONED: 'ausgemustert',
};

function zuletztGesehen(iso?: string | null): { text: string; frisch: boolean } {
  if (!iso) return { text: 'nie', frisch: false };
  const d = new Date(iso);
  const minuten = Math.floor((Date.now() - d.getTime()) / 60000);
  if (minuten < 2) return { text: 'gerade eben', frisch: true };
  if (minuten < 60) return { text: `vor ${minuten} min`, frisch: minuten <= STUMM_MINUTEN };
  const stunden = Math.floor(minuten / 60);
  if (stunden < 24) return { text: `vor ${stunden} h`, frisch: false };
  return {
    text: d.toLocaleDateString('de-DE', {
      timeZone: 'Europe/Berlin',
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }),
    frisch: false,
  };
}

export default function GeraeteListe() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [geraete, setGeraete] = useState<Geraet[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  const lade = useCallback(async () => {
    setLaden(true);
    setFehler('');
    try {
      const res = await fetch('/api/einrichten/objekt/geraete', { credentials: 'include' });
      if (!res.ok) throw new Error('Die Geräteliste konnte nicht geladen werden');
      setGeraete(((await res.json()).objekte ?? []) as Geraet[]);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Geräteliste konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    void lade();
  }, [lade]);

  const wartend = geraete.filter((g) => g.status === 'PENDING');
  const imBetrieb = geraete.filter((g) => g.status === 'APPROVED');
  const stumm = imBetrieb.filter((g) => !zuletztGesehen(g.lastSeenAt).frisch);

  return (
    <LeitstandRahmen titel="Geräte" benutzer={user?.displayName}>
      <div className="flex flex-col gap-6">
        <Link to="/einrichten" className="text-[13px] text-tinte-3 hover:text-tinte-2">
          ← Katalog
        </Link>

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {laden && geraete.length === 0 && !fehler && (
          <p className="py-12 text-center text-sm text-tinte-3">Lade Geräte …</p>
        )}

        {!laden && !fehler && (
          <>
            {wartend.length > 0 && (
              <Notiz titel="Wartet auf Freigabe">
                {wartend.length === 1
                  ? 'Ein Gerät hat sich gemeldet und wartet darauf, freigegeben zu werden.'
                  : `${wartend.length} Geräte haben sich gemeldet und warten auf Freigabe.`}{' '}
                Vor der Freigabe die Kennung mit dem Gerät vor Ort abgleichen.
              </Notiz>
            )}

            {stumm.length > 0 && (
              <Notiz titel="Seit längerem still">
                {stumm.length === 1
                  ? 'Ein freigegebenes Gerät hat'
                  : `${stumm.length} freigegebene Geräte haben`}{' '}
                sich seit über {STUMM_MINUTEN} Minuten nicht gemeldet. Im Normalbetrieb meldet sich
                ein Gerät alle zehn Sekunden.
              </Notiz>
            )}

            <Karte
              titel="Geräte"
              marke={`${imBetrieb.length} im Betrieb${geraete.length > imBetrieb.length ? ` · ${geraete.length - imBetrieb.length} außer Dienst` : ''}`}
              tabelle
            >
              <div className="overflow-x-auto">
                <Tabelle>
                  <thead>
                    <tr>
                      <Kopfzelle>Gerät</Kopfzelle>
                      <Kopfzelle>Aufgabe</Kopfzelle>
                      <Kopfzelle>Anlagen</Kopfzelle>
                      <Kopfzelle>Zustand</Kopfzelle>
                      <Kopfzelle rechts>Zuletzt gemeldet</Kopfzelle>
                    </tr>
                  </thead>
                  <tbody>
                    {geraete.map((g, i) => {
                      const letzte = i === geraete.length - 1;
                      const gesehen = zuletztGesehen(g.lastSeenAt);
                      const aktiv = g.status === 'APPROVED';
                      return (
                        <tr
                          key={g.id}
                          onClick={() => navigate(`/einrichten/geraete/${g.id}`)}
                          className={`cursor-pointer hover:bg-flaeche-2 ${aktiv ? '' : 'opacity-60'}`}
                        >
                          <Zelle letzte={letzte}>
                            {g.deviceId}
                            {g.hostname && g.hostname !== g.deviceId && (
                              <span className="ml-2 text-[12px] text-tinte-3">({g.hostname})</span>
                            )}
                          </Zelle>
                          <Zelle letzte={letzte}>
                            <Kennung>
                              {g.role === 'TWA' ? 'Anlagen-Daten' : (g.role ?? '—')}
                            </Kennung>
                          </Zelle>
                          <Zelle letzte={letzte}>
                            {g.anlagen?.length ? (
                              g.anlagen.map((a) => a.bezeichnung).join(', ')
                            ) : (
                              <span className="text-tinte-3">keine</span>
                            )}
                          </Zelle>
                          <Zelle letzte={letzte}>
                            {g.status === 'APPROVED' && <Zustand art="gut">freigegeben</Zustand>}
                            {g.status === 'PENDING' && <Zustand art="warnung">wartet</Zustand>}
                            {!['APPROVED', 'PENDING'].includes(g.status) && (
                              <span className="font-zahl text-[11px] text-tinte-3">
                                {STATUS_TEXT[g.status] ?? g.status}
                              </span>
                            )}
                          </Zelle>
                          <Zelle rechts zahl letzte={letzte}>
                            <span className={gesehen.frisch ? '' : 'text-tinte-3'}>
                              {gesehen.text}
                            </span>
                          </Zelle>
                        </tr>
                      );
                    })}
                  </tbody>
                </Tabelle>
              </div>
            </Karte>

            <Notiz titel="Wie ein Gerät dazukommt">
              Ein neues Gerät bekommt ein Grund-Linux und den Einrichtungsbefehl; danach meldet es
              sich hier von selbst und wartet auf Freigabe. Erst mit der Freigabe erhält es seinen
              Schlüssel und die Angaben zu den Anlagen — es wird also nicht durch das Aufsetzen zur
              Vertrauensstelle, sondern durch die Freigabe.
            </Notiz>
          </>
        )}
      </div>
    </LeitstandRahmen>
  );
}
